import { ArrowLeft, Printer } from 'lucide-react';
import QRCode from 'qrcode';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/cargofl-ui';
import { useSettings } from '@/lib/admin-api';
import { useAssets } from '@/lib/assets-api';

/**
 * Printable QR stickers for a selection of assets.
 *
 * The point of this page is what it does NOT contain: there is no scanner anywhere in
 * this application. The QR encodes an ordinary https URL to /a/<code>, so the camera
 * app already on every phone reads it and opens the asset - no in-app camera
 * permission, no barcode library on the client, nothing to keep working across iOS
 * releases. Writing a scanner would have been a week of work to replace something
 * every employee already has on their home screen.
 *
 * Rendered OUTSIDE the app shell on purpose. Printing from inside it meant fighting the
 * sidebar and the top bar with print-only CSS, and one missed rule puts navigation
 * chrome on a sheet of stickers.
 */
export function AssetLabelsPage() {
  const [params] = useSearchParams();
  const ids = params.get('ids') ?? '';
  const { data: settings } = useSettings();

  // includeArchived, because a retired asset still has a sticker on it and a label
  // reprint is exactly the moment somebody is relabelling old kit. pageSize matches the
  // API's verify cap - past that this is a job for the printer, not the browser.
  const { data, isPending } = useAssets({ ids, pageSize: 200, includeArchived: true, sort: 'code' });
  const assets = data?.assets ?? [];

  if (!ids) {
    return (
      <Empty>
        <p className="text-sm text-muted-foreground">
          No assets chosen. Tick some rows in the register and choose “Print labels”.
        </p>
      </Empty>
    );
  }

  if (isPending) {
    return (
      <Empty>
        <p className="text-sm text-muted-foreground">Building the sheet…</p>
      </Empty>
    );
  }

  if (assets.length === 0) {
    return (
      <Empty>
        <p className="text-sm text-muted-foreground">
          None of those assets exist any more. Nothing to print.
        </p>
      </Empty>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 print:p-0">
      {/* print:hidden throughout - the sheet that comes out of the printer is stickers
          and nothing else. */}
      <div className="mx-auto mb-6 flex max-w-5xl flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 text-muted-foreground">
            <Link to="/assets">
              <ArrowLeft className="mr-1 size-3.5" /> Register
            </Link>
          </Button>
          <h1 className="text-lg font-bold tracking-[-0.02em]">
            {assets.length} label{assets.length === 1 ? '' : 's'}
          </h1>
          <p className="text-xs text-muted-foreground">
            Each code scans to this asset with any phone camera — no app needed. Print, then
            stick them on.
          </p>
        </div>
        <Button hint={"Print the labels, or save them as PDF"} size="sm" onClick={() => window.print()}>
          <Printer className="mr-1.5 size-4" /> Print
        </Button>
      </div>

      <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 print:gap-2">
        {assets.map((asset) => (
          <Label
            key={asset.id}
            code={asset.code}
            name={asset.name}
            org={settings?.organizationName}
          />
        ))}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
      {children}
      <Button asChild variant="secondary" size="sm">
        <Link to="/assets">Back to the register</Link>
      </Button>
    </div>
  );
}

/**
 * One sticker.
 *
 * `break-inside-avoid` so a label is never split across two sheets - half a QR code is
 * not a slightly worse label, it is an unscannable one.
 */
function Label({ code, name, org }: { code: string; name: string; org?: string }) {
  const png = useQrPng(`${window.location.origin}/a/${encodeURIComponent(code)}`);

  return (
    <div className="flex items-center gap-3 rounded-md border p-3 break-inside-avoid print:border-black/40">
      {/* Fixed square so every label lines up whether or not its QR has rendered yet. */}
      <div className="size-[72px] shrink-0">
        {png && <img src={png} alt="" className="size-full" />}
      </div>
      <div className="min-w-0">
        <div className="at-num text-sm font-bold leading-tight">{code}</div>
        {/* Two lines then clipped. A sticker is 60mm wide and "Dell Latitude 5440
            (Finance, replaced Aug 2026)" has to stop somewhere. */}
        <div className="line-clamp-2 text-[11px] leading-tight text-muted-foreground">{name}</div>
        {org && <div className="mt-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">{org}</div>}
      </div>
    </div>
  );
}

/**
 * Encode one URL as a PNG data URL.
 *
 * A data URL rather than the library's SVG string, because the SVG path would have to
 * go in through dangerouslySetInnerHTML. 512px is roughly 500dpi at sticker size, so it
 * prints sharp; error correction stays at the default M, which survives a scuffed
 * sticker without making the pattern too dense to read from a phone's distance.
 */
function useQrPng(text: string): string | null {
  const [png, setPng] = React.useState<string | null>(null);

  React.useEffect(() => {
    let live = true;
    QRCode.toDataURL(text, { margin: 1, width: 512, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (live) setPng(url);
      })
      // A label with no QR still carries a readable code, so a failure here degrades to
      // a text-only sticker rather than an error screen.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [text]);

  return png;
}
